import {
  Save,
  RotateCcw,
  Mail,
  MessageSquare,
  Webhook,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';

const SystemSettingsNotifications = ({
  form,
  onSubmit,
  saving,
  resetToDefaults,
}) => {
  return (
    <TabsContent value='notifications'>
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Configure email, SMS, webhook notifications and alert preferences.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Mail className='h-5 w-5 mr-2' />
                  Notification Channels
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <FormField
                    control={form.control}
                    name='emailNotificationsEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base flex items-center'>
                            <Mail className='h-4 w-4 mr-2' />
                            Email Notifications
                          </FormLabel>
                          <FormDescription>
                            Send notifications via email.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='smsNotificationsEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base flex items-center'>
                            <MessageSquare className='h-4 w-4 mr-2' />
                            SMS Notifications
                          </FormLabel>
                          <FormDescription>
                            Send notifications via SMS.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='webhookNotificationsEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base flex items-center'>
                            <Webhook className='h-4 w-4 mr-2' />
                            Webhook Notifications
                          </FormLabel>
                          <FormDescription>
                            Send notifications via webhooks.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium'>Delivery Settings</h3>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormField
                    control={form.control}
                    name='notificationRetryAttempts'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Retry Attempts</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={1}
                              max={5}
                              step={1}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value} retry attempts
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Number of retry attempts for failed notifications.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='notificationTimeout'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notification Timeout (seconds)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={5}
                              max={300}
                              step={5}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value} seconds timeout
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Timeout for notification delivery attempts.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <AlertTriangle className='h-5 w-5 mr-2' />
                  Alert Types
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <FormField
                    control={form.control}
                    name='adminEmailAlerts'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            Admin Alerts
                          </FormLabel>
                          <FormDescription>
                            Send alerts to administrators for critical events.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='userStatusUpdates'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            User Status Updates
                          </FormLabel>
                          <FormDescription>
                            Notify users about KYC status changes.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='systemHealthAlerts'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            System Health Alerts
                          </FormLabel>
                          <FormDescription>
                            Send alerts for system health issues.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium'>Email Configuration</h3>
                <div className='bg-muted/50 p-4 rounded-lg'>
                  <p className='text-sm text-muted-foreground mb-4'>
                    Configure SMTP settings for email notifications. These
                    settings are managed separately for security.
                  </p>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium'>SMTP Server</label>
                      <Input
                        placeholder='smtp.example.com'
                        disabled
                        className='bg-background'
                      />
                      <p className='text-xs text-muted-foreground'>
                        Configured via environment variables
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium'>
                        From Address
                      </label>
                      <Input
                        placeholder='noreply@example.com'
                        disabled
                        className='bg-background'
                      />
                      <p className='text-xs text-muted-foreground'>
                        Configured via environment variables
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium'>Webhook Configuration</h3>
                <div className='bg-muted/50 p-4 rounded-lg'>
                  <p className='text-sm text-muted-foreground mb-4'>
                    Configure webhook endpoints for external integrations.
                  </p>
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <label className='text-sm font-medium'>Webhook URL</label>
                      <Input
                        placeholder='https://your-webhook-endpoint.com/notifications'
                        disabled
                        className='bg-background'
                      />
                      <p className='text-xs text-muted-foreground'>
                        Webhook endpoints are managed in the integrations
                        section
                      </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <label className='text-sm font-medium'>
                          Authentication
                        </label>
                        <Input
                          placeholder='Bearer token or API key'
                          type='password'
                          disabled
                          className='bg-background'
                        />
                      </div>
                      <div className='space-y-2'>
                        <label className='text-sm font-medium'>
                          Content Type
                        </label>
                        <Input
                          placeholder='application/json'
                          disabled
                          className='bg-background'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => resetToDefaults(form, 'notifications')}
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Reset to Defaults
                </Button>
                <Button type='submit' disabled={saving}>
                  <Save className='h-4 w-4 mr-2' />
                  {saving ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default SystemSettingsNotifications;
